<script setup>
import { computed, getCurrentInstance } from 'vue';
import { clampDate, formatDatePtBr } from './datePickerUtils';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  id: { type: String, default: '' },
  min: { type: String, default: '' },
  max: { type: String, default: '' },
  placeholder: { type: String, default: 'dd/mm/aaaa' },
  message: { type: String, default: '' },
  messageType: {
    type: String,
    default: 'info',
    validator: value => ['info', 'error', 'success'].includes(value),
  },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus']);

const { uid } = getCurrentInstance();
const inputId = computed(() => props.id || `lais-date-picker-${uid}`);
const readableValue = computed(() => formatDatePtBr(props.modelValue));

const messageClass = computed(() => ({
  'text-lais-text-helper': props.messageType === 'info',
  'text-lais-danger': props.messageType === 'error',
  'text-lais-success': props.messageType === 'success',
}));

const handleInput = event => {
  emit(
    'update:modelValue',
    clampDate(event.target.value, { min: props.min, max: props.max })
  );
};
</script>

<template>
  <label class="flex min-w-0 flex-col gap-1" :for="inputId">
    <span v-if="label" class="text-lais-body-sm font-semibold text-lais-text">
      {{ label }}
    </span>
    <span
      class="relative flex h-10 items-center rounded-lais border border-lais-border bg-lais-bg-card text-lais-body-md text-lais-text transition-colors focus-within:border-lais-primary focus-within:outline focus-within:outline-2 focus-within:outline-lais-primary-50"
    >
      <span
        class="i-lucide-calendar-days pointer-events-none ml-3 size-4 text-lais-icon"
        aria-hidden="true"
      />
      <input
        :id="inputId"
        class="h-full min-w-0 flex-1 border-0 bg-transparent px-3 text-lais-body-md text-lais-text outline-none placeholder:text-lais-text-muted disabled:cursor-not-allowed disabled:opacity-60"
        type="date"
        :value="modelValue"
        :min="min || undefined"
        :max="max || undefined"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :aria-invalid="messageType === 'error'"
        @input="handleInput"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
      />
    </span>
    <span v-if="readableValue" class="text-lais-label-sm text-lais-text-helper">
      {{ readableValue }}
    </span>
    <span v-if="message" class="text-lais-label-sm" :class="messageClass">
      {{ message }}
    </span>
  </label>
</template>
